import { ReactElement, useMemo } from 'react'
import { useWatch } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useModelStore } from '@editorStores'
import { useSceneLineMarker } from '@editorHooks'
import { getWindLoadSettings } from '@queries'
import useStoreysForSeismic from '../../../../hooks/useStoreysForSeismic'
import { FocusedDimension, toStoreyDimensionLines } from './utils'

interface Props {
  /** The Länge/Breite field with focus, whose two edges are drawn highlighted. */
  focused?: FocusedDimension
}

const BASE_COLOR = '#105ba4'
const HIGHLIGHT_COLOR = '#e32f27'

/**
 * Draws the configured seismic plan dimensions as a rectangle per storey, at
 * that storey's ceiling level, so the configured box can be checked against the
 * model storey by storey.
 *
 * Reads the live form values rather than the saved configuration, so the
 * rectangles follow the Länge/Breite fields while they are being edited. This
 * component therefore has to be rendered inside the configuration form.
 */
const SeismicDimensionsSceneMarkers = ({ focused }: Props): ReactElement => {
  const { projectId }: { projectId?: string } = useParams()

  const storeyBoundaries = useModelStore(state => state.model.storey_boundaries)
  const storeys = useStoreysForSeismic()

  const buildingDimensions = useWatch({ name: 'building_dimensions' })

  const { data: windLoadSettings } = useQuery({
    queryKey: getWindLoadSettings.getKey(projectId),
    queryFn: () => getWindLoadSettings.request(projectId),
  })

  const origin = windLoadSettings?.wallBuildingDimensions?.start

  const { baseLines, highlightedLines } = useMemo(
    () =>
      toStoreyDimensionLines({
        storeys,
        storeyBoundaries,
        buildingDimensions,
        origin,
        focused,
      }),
    [storeys, storeyBoundaries, buildingDimensions, origin, focused],
  )

  useSceneLineMarker({
    lines: baseLines,
    enabled: baseLines.length > 0,
    color: BASE_COLOR,
    lineWidth: 3,
    renderOrder: 1000,
    disableDepthTest: true,
  })

  useSceneLineMarker({
    lines: highlightedLines,
    enabled: highlightedLines.length > 0,
    color: HIGHLIGHT_COLOR,
    lineWidth: 5,
    renderOrder: 1001,
    disableDepthTest: true,
  })

  return <></>
}

export default SeismicDimensionsSceneMarkers
